import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { workerCostDigestAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PillToggle from '../UI/PillToggle';
import toast from '../../utils/toast';
import { downloadBlobResponse } from '../../utils/downloadBlob';
import { formatDateTimeCompact } from '../../utils/dateUtils';

const QUERY_KEY = ['worker-cost-digest', 'admin'];

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const WorkerCostDigestCard = () => {
  const { user } = useAuth();
  const canView = Boolean(user?.has_admin_access || user?.is_superuser);
  const queryClient = useQueryClient();
  const [staffId, setStaffId] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await workerCostDigestAPI.getAdmin();
      return response.data;
    },
    enabled: canView,
    staleTime: 30 * 1000,
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const saveSettings = useMutation({
    mutationFn: (payload) => workerCostDigestAPI.updateAdmin(payload),
    onSuccess: (response, payload) => {
      queryClient.setQueryData(QUERY_KEY, response.data);
      if (payload.add_staff_id) {
        toast.success(`${payload.add_staff_id} will receive the file.`);
        setStaffId('');
      } else if (payload.remove_staff_id) {
        toast.success(`${payload.remove_staff_id} will no longer receive the file.`);
      } else if (typeof payload.enabled === 'boolean') {
        toast.success(payload.enabled ? 'Worker cost email is on.' : 'Worker cost email is off.');
      }
    },
    onError: (error) => {
      toast.error(errorMessage(error, 'Could not save the worker cost settings.'));
    },
  });

  const sendNow = useMutation({
    mutationFn: (force) => workerCostDigestAPI.sendNow(force),
    onSuccess: (response) => {
      queryClient.setQueryData(QUERY_KEY, response.data);
      if (response.data?.send_result === 'partial') {
        toast.warning(response.data.send_detail || 'Some recipients did not get the file.');
      } else {
        toast.success(response.data?.send_detail || 'Worker cost file emailed.');
      }
    },
    onError: (error) => {
      if (error?.response?.data) {
        queryClient.setQueryData(QUERY_KEY, error.response.data);
      } else {
        refresh();
      }
      toast.error(errorMessage(error, 'Could not email the worker cost file.'));
    },
  });

  const downloadFile = useMutation({
    mutationFn: async (file) => {
      const response = await workerCostDigestAPI.downloadAdmin(file.id);
      downloadBlobResponse(response, file.file_name || 'Worker_Cost.xlsx');
    },
    onSuccess: () => toast.success('File downloaded.'),
    onError: () => toast.error('Could not download the file.'),
  });

  if (!canView) return null;

  const recipients = data?.recipients || [];
  const recent = data?.recent_files || [];
  const canEmail = Boolean(data?.can_send || data?.can_resend);
  const sending = sendNow.isPending;

  const addRecipient = (event) => {
    event.preventDefault();
    const value = staffId.trim().toUpperCase();
    if (!value) return;
    saveSettings.mutate({ add_staff_id: value });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden col-span-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900">Worker cost file</h3>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
            {isLoading ? '…' : `${recipients.length} ${recipients.length === 1 ? 'recipient' : 'recipients'}`}
          </span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Emails each worker&apos;s contractual basic salary and levy. Onboarded and offboarded rows are highlighted, with date joined and employment end date. Onboarded means they were not on the previous month&apos;s file. Offboarded means their employment end date falls in the report month. It waits for the MOM levy bill, then goes out at 12:00 PM on the 5th, or the next working day.
        </p>
      </div>

      {isLoading ? (
        <div className="px-6 py-8 flex items-center justify-center gap-2 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading worker cost settings...</span>
        </div>
      ) : isError ? (
        <div className="px-6 py-8 text-sm text-gray-500">
          The worker cost settings could not be loaded.
        </div>
      ) : (
        <div className="px-6 py-5 space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Email the file</p>
              <p className="text-sm text-gray-500">Off until you turn it on. Nothing sends while this is off.</p>
            </div>
            <PillToggle
              theme="admin"
              layout="sectionHeader"
              ariaLabel="Worker cost email"
              value={data.enabled ? 'on' : 'off'}
              onChange={(value) => {
                const next = value === 'on';
                if (next === data.enabled) return;
                saveSettings.mutate({ enabled: next });
              }}
              options={[
                { id: 'off', label: 'Off', disabled: saveSettings.isPending },
                { id: 'on', label: 'On', disabled: saveSettings.isPending },
              ]}
            />
          </div>

          <div className="rounded-xl border border-gray-200 px-4 py-4 space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-gray-900">{data.period_label}</p>
              <p className="text-sm text-gray-500">Sends {data.send_date_label}</p>
            </div>
            <p className="text-sm text-gray-600">
              {data.bill_uploaded
                ? `${data.period_label} levy bill is uploaded.`
                : `${data.period_label} levy bill is not uploaded.`}
            </p>
            {data.block_reason ? (
              <p className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                {data.block_reason}
              </p>
            ) : null}
            <button
              type="button"
              className="btn btn-admin-primary"
              disabled={!canEmail || sending}
              onClick={() => sendNow.mutate(Boolean(data.can_resend))}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {data.can_resend ? 'Send again' : 'Send now'}
            </button>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-3">Recipients</p>
            {recipients.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">Nobody receives this file yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 mb-4">
                {recipients.map((recipient) => (
                  <li key={recipient.staff_id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {recipient.full_name}
                        <span className="ml-2 text-gray-400 font-normal">{recipient.staff_id}</span>
                      </p>
                      <p className="text-sm text-gray-500 truncate">{recipient.work_email || 'No work email'}</p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-admin-outline btn-sm flex-shrink-0"
                      disabled={saveSettings.isPending}
                      onClick={() => saveSettings.mutate({ remove_staff_id: recipient.staff_id })}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={addRecipient} className="flex flex-col gap-2 sm:flex-row">
              <input
                className="input-admin sm:max-w-xs"
                value={staffId}
                onChange={(event) => setStaffId(event.target.value)}
                placeholder="Staff ID"
                aria-label="Recipient staff ID"
                autoCapitalize="characters"
              />
              <button type="submit" className="btn btn-admin-secondary" disabled={saveSettings.isPending || !staffId.trim()}>
                Add recipient
              </button>
            </form>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-3">Files already emailed</p>
            {recent.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing has been emailed yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recent.map((file) => (
                  <li key={file.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{file.period_label}</p>
                      <p className="text-sm text-gray-500">
                        {file.recipient_full_name || file.recipient_staff_id}
                        {' · '}
                        {file.worker_count} workers
                        {' · '}
                        {file.joined_count ?? 0} onboarded
                        {' · '}
                        {file.left_count ?? 0} offboarded
                        {file.sent_at ? ` · ${formatDateTimeCompact(file.sent_at)}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-admin-secondary btn-sm self-start"
                      disabled={downloadFile.isPending}
                      onClick={() => downloadFile.mutate(file)}
                    >
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerCostDigestCard;
